<template>
  <div class="IndicatorPopup PlatformMapPopup PlatformMapPopup--project MyMapProjectPopupContent">
    <router-link
      class="PlatformMapPopup__linkedBlock PlatformMapPopup__linkedBlock--projectCard"
      :to="projectPageRoute"
    >
      <div class="ProjectCard MyMapProjectPopupContent__projectCard">
        <div class="ProjectCard__content">
          <div class="ProjectCard__block ProjectCard__block--left">
            <span v-if="geoSubTitle !== ''" class="InfoCard__subTitle">{{ geoSubTitle }}</span>
            <span class="InfoCard__title">{{ displayTitle }}</span>
            <div class="ProjectCard__infoCtn">
              <span v-if="actorDisplayName !== ''" class="MyMapProjectPopupContent__actor">{{
                actorDisplayName
              }}</span>
              <span v-if="formattedUpdatedAt !== ''" class="ProjectCard__updatedAt">
                {{ $t('labels.updatedAt') }} {{ formattedUpdatedAt }}
              </span>
            </div>
          </div>
          <div
            v-if="coverImageUrl !== ''"
            class="ProjectCard__block ProjectCard__block--right"
          >
            <img
              loading="lazy"
              class="ProjectCard__logo"
              :src="coverImageUrl"
              :alt="coverImageAlt"
            />
          </div>
        </div>

        <div
          v-if="hasDescription"
          class="PlatformMapPopup__projectDescription PlatformMapPopup__projectDescription--html"
          v-html="sanitizedDescriptionHtml"
        />

        <span v-if="statusTagLabel !== ''" class="PlatformMapPopup__tag PlatformMapPopup__tag--project">
          {{ statusTagLabel }}
        </span>
      </div>
    </router-link>
  </div>
</template>

<script setup lang="ts">
import type { Project } from '@/models/interfaces/Project'
import { resolveMediaObjectDisplayUrl } from '@/config/env'
import { sanitizeRichTextHtml } from '@/services/utils/sanitizeRichTextHtml'
import GeocodingService from '@/services/map/GeocodingService'
import {
  GEOPORTAIL_MODE_QUERY_KEY,
  GEOPORTAIL_COMMUNE_MODE_QUERY,
} from '@/services/commune/navigateGeoportailCommune'
import { localizeDate } from '@/services/utils/UtilsService'
import { useMyMapStore } from '@/stores/myMapStore'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  project: Project
}>()

const { t, te } = useI18n()
const mapStore = useMyMapStore()

/**
 * Carries the communal geoportail's color scheme over to the project detail page when the
 * project was clicked from the Geoportail communal (vs. the central geoportail, which keeps
 * the default theme). See ProjectDetailView.vue and MyMapView-commune-theme.scss.
 */
const projectPageRoute = computed(() => ({
  name: 'projectPage',
  params: { id: props.project.id },
  query:
    mapStore.lockedCommuneName != null ? { [GEOPORTAIL_MODE_QUERY_KEY]: GEOPORTAIL_COMMUNE_MODE_QUERY } : undefined,
}))

const displayTitle = computed(() => {
  const nameRaw = props.project.name
  return nameRaw != null && String(nameRaw).trim() !== '' ? String(nameRaw).trim() : '—'
})

const coverImageUrl = computed(() => {
  const fromLogo = resolveMediaObjectDisplayUrl(props.project.logo)
  if (fromLogo !== '') return fromLogo
  const list = props.project.images
  if (!Array.isArray(list) || list.length === 0) return ''
  return resolveMediaObjectDisplayUrl(list[0])
})

const coverImageAlt = computed(() => displayTitle.value)

function scopeLabelsLine(): string {
  const scopes = props.project.administrativeScopes ?? []
  if (scopes.length === 0) return ''
  return scopes
    .map((s) => {
      const key = `projects.scope.${s}`
      return te(key) ? String(t(key)) : String(s)
    })
    .join(', ')
}

function statusLabel(): string {
  const s = props.project.status
  if (s == null || String(s).trim() === '') return ''
  const key = `projects.status.${s}`
  return te(key) ? String(t(key)) : String(s)
}

/** Same idea as ProjectCard: geo line; enrich with API-only fields when name is short or missing. */
const geoSubTitle = computed(() => {
  const geo = props.project.geoData
  const name = geo?.name?.trim()
  if (name != null && name !== '') return name
  const other = props.project.otherAdministrativeScopes?.trim()
  if (other != null && other !== '') return other
  const scopes = scopeLabelsLine()
  if (scopes !== '') return scopes
  if (geo != null) {
    const fallback = GeocodingService.getLocationName(geo).trim()
    if (fallback !== '') return fallback
  }
  return ''
})

const actorDisplayName = computed(() => {
  const n = props.project.actor?.name?.trim()
  return n != null && n !== '' ? n : ''
})

const formattedUpdatedAt = computed(() => {
  const dateRaw = props.project.updatedAt
  if (dateRaw == null) return ''
  return localizeDate(dateRaw, { year: 'numeric', month: 'long', day: 'numeric' })
})

const statusTagLabel = computed(() => {
  const st = statusLabel()
  if (st !== '') return st
  const sector = props.project.sector?.trim()
  if (sector != null && sector !== '') return sector
  return t('myMap.projectPopup.tagOther')
})

const descriptionText = computed(() =>
  props.project.description != null ? String(props.project.description).trim() : '',
)

const hasDescription = computed(() => descriptionText.value !== '')

const sanitizedDescriptionHtml = computed(() => sanitizeRichTextHtml(descriptionText.value))

</script>

<style lang="scss" scoped>
@use './indicator-popup.shared.scss' as *;
@use './platform-map-popup.shared.scss' as *;

/* Mirror ProjectCard map row + InfoCard typography (popup is outside InfoCard.v-card). */
.MyMapProjectPopupContent__projectCard.ProjectCard {
  margin: 0;

  .ProjectCard__content {
    display: flex;
    flex-flow: row nowrap;
    margin: 0 0 0.65rem;
    padding-right: 1.75rem;
    justify-content: space-between;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .ProjectCard__block--left {
    display: flex;
    flex-flow: column nowrap;
    gap: 0.35rem;
    flex: 1;
    min-width: 0;
  }

  .ProjectCard__block--right {
    flex-shrink: 0;
    height: 70px;
    width: 70px;

    .ProjectCard__logo {
      max-width: 100%;
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 4px;
    }
  }

  .ProjectCard__infoCtn {
    display: flex;
    flex-flow: column nowrap;
    gap: 0.35rem;

    .MyMapProjectPopupContent__actor {
      font-size: 0.8125rem;
      color: $platform-popup-body-text;
    }

    .ProjectCard__updatedAt {
      font-size: var(--font-size-xs, 0.75rem);
      color: $platform-popup-accent-text;
    }
  }

  .InfoCard__subTitle {
    font-size: 14px;
    font-weight: 700;
    color: rgb(var(--v-theme-light-blue));
    line-height: 1.35;
    overflow-wrap: break-word;
    word-break: normal;
  }

  .InfoCard__title {
    font-size: 18px;
    font-weight: 700;
    color: rgb(var(--v-theme-main-blue));
    text-transform: uppercase;
    line-height: 1.25;
    overflow-wrap: break-word;
    word-break: normal;
  }
}

.PlatformMapPopup__projectDescription {
  margin: 0 0 0.5rem;
  font-size: 0.875rem;
  line-height: 1.5;
  color: $platform-popup-body-text;
  overflow-wrap: break-word;

  &--html {
    :deep(p) {
      margin: 0 0 0.5rem;

      &:last-child {
        margin-bottom: 0;
      }
    }

    :deep(ul),
    :deep(ol) {
      margin: 0 0 0.5rem;
      padding-left: 1.25rem;
    }

    :deep(li) {
      margin-bottom: 0.25rem;
    }

    :deep(h1),
    :deep(h2),
    :deep(h3),
    :deep(h4),
    :deep(h5),
    :deep(h6) {
      margin: 0.65rem 0 0.35rem;
      font-weight: 700;
      line-height: 1.3;

      &:first-child {
        margin-top: 0;
      }
    }

    :deep(blockquote) {
      margin: 0 0 0.5rem;
      padding-left: 0.75rem;
      border-left: 2px solid rgba(var(--v-theme-main-blue), 0.35);
    }

    :deep(code) {
      font-size: 0.85em;
      padding: 0.1em 0.3em;
      border-radius: 3px;
      background: rgba(0, 0, 0, 0.06);
    }

    :deep(pre) {
      margin: 0 0 0.5rem;
      padding: 0.5rem 0.65rem;
      overflow-x: auto;
      border-radius: 4px;
      background: rgba(0, 0, 0, 0.06);
      font-size: 0.8125rem;
    }

    :deep(pre code) {
      padding: 0;
      background: transparent;
    }

    :deep(a) {
      color: rgb(var(--v-theme-main-blue));
      text-decoration: underline;
    }
  }
}

.PlatformMapPopup__tag--project {
  margin-top: 0.15rem;
}
</style>
